import type { ModelTarget } from '../surfaces/model-target.ts'
import type { ComponentTarget } from '../stable-surfaces/target.ts'
import type { DefinitionId, SurfaceId } from '../stable-surfaces/id.ts'
import type { RoutePattern } from '../polish/sightmap/route.ts'

// src/guidance/journey/check-types.ts — the pure, dependency-free declarations for a journey CHECK.
//
// Split out of ./steps.ts so an app-reachable module can name a `JourneyCheck` WITHOUT importing steps.ts.
// steps.ts pulls in the whole journey runtime (Playwright, the recording/browser-history reader, the
// layout-shift rrweb engine), and those modules import harness-siblings (event-chunks.ts / event-stream.ts)
// that only resolve inside the guidance bundler — not under the app `tsc`. A netlify function or a shared
// schema that only needs the check SHAPE would otherwise drag that entire graph into the app typecheck and
// fail to resolve those siblings (see netlify/functions/lib/critic-items.ts, which restates a steps.ts type
// for exactly this reason). Keep this file free of runtime imports so it stays safe to import from anywhere.
//
// steps.ts re-exports these, so its existing importers are unchanged.

export const CHECK_ASSERTIONS = [
  'exists',
  'not_exists',
  'visible',
  // Some area of exactly one rendered target intersects the viewport after every clipping ancestor
  // (viewport.ts measureInView). `visible` is CSS visibility only: it holds for an offscreen target.
  'in_view',
  'contains_text',
  'has_value',
  'has_state',
  'has_count',
  'url_contains',
  // An ABSTRACT check: `description` is a plain-English condition the MODEL evaluates against the live
  // page state (no selector/expected, no deterministic assertion). It is the check-side twin of the
  // 'abstract' action: steps.ts does NOT evaluate it (evaluatePageCheck passes it through) — the runner
  // routes an abstract check to its model-backed evaluateAbstractCheck handler, which judges whether the
  // described condition actually holds and files a bug when it does not.
  'abstract',
  // Surface postcondition: deterministic recognition first, ordinary abstract learning otherwise.
  'reachSurfaceCheck',
] as const

/** Assertions that read a target's instance set, with params as count filters, rather than one instance. */
export function countsInstances(assertion: string): assertion is 'has_count' | 'not_exists' {
  return assertion === 'has_count' || assertion === 'not_exists'
}

export type CheckAssertion = (typeof CHECK_ASSERTIONS)[number]

// TODO(step-action typed storage, docs/step-action/model.md): store one `TargetRef` (`selector` → `{locator}`, `component` → `{component}`) instead of the `LegacyStepTargetFields` encodings.
export interface JourneyCheck {
  /** Model input only; compiled to component before execution or storage. */
  target?: ModelTarget
  property?: string
  component?: ComponentTarget
  description: string
  selector?: string
  // The discovered-interaction label this check's target resolved from (set at author time by the
  // journey generator). Its PRESENCE marks `selector` as machine-resolved from a real, discovered DOM
  // interaction (not model-authored) — the journey judge trusts a labeled target and does not vet its
  // selector. The runner ignores this field; it drives off `selector`.
  label?: string
  surfaceId?: SurfaceId
  controlId?: DefinitionId
  assertion: CheckAssertion
  expected?: string
  /** `url_contains` only, in place of `expected`: the route the URL's path is on. */
  route?: RoutePattern
}

/** Why an expectation was dismissed: the judge's ruling, the failure handler's removal, a relocation, or the recovery that replaced the asserted action. */
export const CHECK_DISMISSALS = ['invalid_check', 'already_past_gate', 'handler_removed', 'relocated', 'orphaned'] as const
export type CheckDismissal = typeof CHECK_DISMISSALS[number]
